import React from 'react';
import { buildURI } from 'react-csv/src/core';
import { Redirect } from 'react-router';

import { Box, Collapse, Container, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CloudDownloadOutlined } from '@mui/icons-material';
import { useCurrentuser } from '@uplift/contexts';
import { useIsMobile, useL10n, useUpBreakpoint } from '@uplift/hooks';
import { CalendarDateService, DateFormats, TimeWindow } from '@uplift/services';
import {
  Alert, Button, Loader, ProviderLayout, ProviderLayoutPageHeader, UpliftThemeProvider,
  useOnScreenObserver
} from '@uplift/ui-v2';

import { FinanceTable } from './components/FinanceSummaryTable';
import { MonthRangeFilter } from './components/MonthRangeFilter';
import { useFinanceFilters } from './finance.hooks';
import { convertFinanceRowsToCsvData } from './helpers';
import { useFinanceMonthsQuery, useFinancesTimeRangeQuery, useFinancesTotalQuery } from '@uplift/queries';
import { getFinanceMonths } from '@uplift/api';


const useFinanceStyles = makeStyles()(theme => ({
  container: {
    paddingTop: 0,
    paddingBottom: 0,
    backgroundColor: theme.palette.common.white,

    [theme.breakpoints.up('sm')]: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      backgroundColor: 'transparent',
    }
  }
}));

const DownloadReportButton: React.VFC<{
  providerId: string;
  filter: TimeWindow;
}> = ({ providerId, filter }) => {
  const [ downloading, setDownloading ] = React.useState(false);
  const l10n = useL10n();
  const isMobile = useIsMobile();
  const disabled = downloading;

  return (
    <Button
      variant="outlined"
      startIcon={<CloudDownloadOutlined color={disabled ? 'disabled' : 'action'} />}
      size={isMobile ? 'small' : 'medium'}
      onClick={async () => {
        setDownloading(true);

        const response = await getFinanceMonths(providerId, {
          rangeFrom: filter.start.format(DateFormats.month),
          rangeTo: filter.end.format(DateFormats.month),
          limit: null,
        });

        const periods = response.data;
        const csvFileName = l10n.finance.csvTitle(CalendarDateService.date());

        const csv = { ...convertFinanceRowsToCsvData(periods), csvFileName };
        const csvURL = buildURI(csv.csvData, undefined, csv.csvHeaders);
        const a = document.createElement('a');

        a.download = csv.csvFileName;
        a.href = csvURL;
        a.target = '_blank';
        a.click();
        setDownloading(false);
      }}
      loading={downloading}
      disabled={disabled}
    >
      {l10n.general.download}
    </Button>
  );
};


const FinancePageContent: React.VFC<{ providerId: string; msoPayout: boolean }> = ({ providerId, msoPayout }) => {
  const { classes } = useFinanceStyles();
  const isSmUp = useUpBreakpoint('sm');
  const l10n = useL10n();
  const { data: financesTotal, isLoading: isFinancesTotalLoading } = useFinancesTotalQuery(providerId, { suspense: false });
  const { data: financesTimeRange } = useFinancesTimeRangeQuery(providerId, { suspense: true });
  const { rangeFilter, setRangeFilter, serverRange } = useFinanceFilters(financesTimeRange);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useFinanceMonthsQuery({
    providerId: providerId,
    rangeFrom: rangeFilter.start.format(DateFormats.month),
    rangeTo: rangeFilter.end.format(DateFormats.month),
  }, { suspense: true, keepPreviousData: true });
  const periods = data.pages.flat();
  const hasCalculationsWarning = financesTotal?.hasCalculationsWarning || periods?.some(p => p.hasCalculationsWarning);

  const screenObserverRef = useOnScreenObserver(() => {
    if (!isFetchingNextPage) {
      fetchNextPage();
    }
  });

  return (
    <>
      <ProviderLayoutPageHeader
        title={l10n.finance.title}
        actions={(
          <>
            <MonthRangeFilter
              value={rangeFilter}
              onChange={setRangeFilter}
              availableRange={serverRange}
              disabled={!financesTimeRange}
            />
            {isSmUp && (
              <DownloadReportButton
                providerId={providerId}
                filter={rangeFilter}
              />
            )}
          </>
        )}
      >
        {msoPayout && (
          <Container>
            <Box mt={{ xs: 0, sm: -3 }} mb={3}>
              <Typography variant="body2" color="textPrimary">
                {l10n.finance.payoutSubtitle}
              </Typography>
            </Box>
          </Container>
        )}
      </ProviderLayoutPageHeader>

      <Container className={classes.container}>
        <Collapse in={!!hasCalculationsWarning}>
          <Box mb={3}>
            <Alert severity="info">{l10n.finance.summary.calculationWarning}</Alert>
          </Box>
        </Collapse>

        <Box>
          <FinanceTable
            rows={periods}
            total={financesTotal}
            totalLoading={isFinancesTotalLoading}
          />
          {hasNextPage && (
            <div ref={screenObserverRef}>
              <Loader color="secondary" size="2rem" mt={3} />
            </div>
          )}
        </Box>
      </Container>
    </>
  );
};


export const FinancePage: React.FC = () => {
  const me = useCurrentuser();

  if (me.fullTime) {
    return <Redirect to="/main" />;
  }

  return (
    <ProviderLayout>
      <UpliftThemeProvider>
        <FinancePageContent
          providerId={me.id}
          msoPayout={!!me.rules?.msoPayout}
        />
      </UpliftThemeProvider>
    </ProviderLayout>
  );
};
